import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import * as api from './api'

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; user: api.User }

type AuthContextValue = AuthState & {
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /** Re-fetch /me/ — call after impersonation swap. */
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

// Structured trace logger for auth/acting-as transitions. We've seen
// the impersonation indicator flicker off-and-back-on intermittently;
// without a trail it's impossible to chase. Every state change here
// (mount, refresh, login, logout, 401) gets a tagged log line so we
// can see the sequence after the fact. Stamp format: `[auth:source]`.
function authLog(source: string, payload: Record<string, unknown>) {
  // eslint-disable-next-line no-console
  console.info(`[auth:${source}]`, { t: new Date().toISOString(), ...payload })
}

function summarize(user: api.User | null): Record<string, unknown> {
  if (!user) return { user: null }
  return {
    username: user.username,
    role: user.role,
    is_superuser: user.is_superuser,
    acting_as: user.acting_as
      ? { real_username: user.acting_as.real_username, real_is_superuser: user.acting_as.real_is_superuser }
      : null,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  useEffect(() => {
    authLog('mount', { note: 'fetching /me' })
    api.me().then((user) => {
      authLog('mount-response', summarize(user))
      setState(user ? { status: 'authenticated', user } : { status: 'unauthenticated' })
    })
  }, [])

  // Wire the global 401-interceptor handler in api.ts. When any API call
  // (other than the auth endpoints themselves) returns 401, this flips
  // React state to unauthenticated → AuthProvider re-renders → App.tsx's
  // unauthenticated branch redirects to /login. Catches mid-session
  // revocation, cross-tab logout, server session expiry. Idempotent
  // (calling setState to the same value is a no-op).
  useEffect(() => {
    api.setUnauthHandler(() => {
      authLog('401-handler', { note: 'forcing unauthenticated' })
      setState((prev) => prev.status === 'authenticated' ? { status: 'unauthenticated' } : prev)
    })
    return () => api.setUnauthHandler(null)
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    const user = await api.login(username, password)
    authLog('login', summarize(user))
    setState({ status: 'authenticated', user })
  }, [])

  const logout = useCallback(async () => {
    authLog('logout', {})
    await api.logout()
    setState({ status: 'unauthenticated' })
  }, [])

  const refresh = useCallback(async () => {
    authLog('refresh', { note: 'fetching /me' })
    const user = await api.me()
    authLog('refresh-response', summarize(user))
    setState(user ? { status: 'authenticated', user } : { status: 'unauthenticated' })
  }, [])

  return <AuthContext.Provider value={{ ...state, login, logout, refresh }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth outside AuthProvider')
  return ctx
}
